import { AufnahmePunktDocument } from '../data-model';
import { karbonatEval } from '../bohrpunkte/evaluators/karbonat';
import { angezeigteTiefe, filzVersatz } from '../bohrpunkte/filz-tiefen';

/** Die beiden Tiefen-Spalten des GIS-Exports; fehlt ein Wert, fehlt der Schluessel. */
export interface ExportTiefen {
	Kalktiefe?: number;
	Spiegeltiefe?: number;
}

/**
 * Kalktiefe fuer den Export, mit derselben Herkunft wie im Protokoll: Beim
 * vollen Protokoll rechnet die App die Karbonattiefe aus der Substratfolge
 * (`karbonatEval`, erste Zeile mit Karbonatgehalt), und genau das zeigen
 * Boden-Reiter und PDF. Gibt die Substratfolge nichts her, gilt das Feld
 * `extendedHeader.karbonat` - im Kurzprotokoll (9990/9998/9999) ist es die
 * Eingabe von Hand, beim vollen Protokoll der eingelesene Wert des Imports.
 */
export function kalktiefeFuerExport(bp: AufnahmePunktDocument): number | undefined {
	const gerechnet = karbonatEval(bp) as number | undefined;
	// Bei einem Filz zaehlen die Tiefen ab Oberkante Ol (filz-tiefen.ts,
	// Nutzer-Entscheidung 25.09.2026: der Export gibt verschobene Werte aus).
	// Der Auswerter liefert schon verschoben; das Feld von Hand wird hier
	// verschoben.
	return istZahl(gerechnet) ? gerechnet : angezeigteTiefe(zahlOderNichts(bp.extendedHeader?.karbonat), filzVersatz(bp));
}

/**
 * Kalktiefe und Spiegeltiefe fuer den GIS-Export (Nutzer-Auftrag 24.09.2026,
 * seit demselben Tag fuer ALLE Punktarten - auch das Langprotokoll traegt die
 * Spalten). Spiegeltiefe ist `extendedHeader.spiegelTiefe` (Boden-Maske bzw.
 * Kurzprotokoll), Kalktiefe siehe `kalktiefeFuerExport`.
 *
 * Ein leerer Wert wird WEGGELASSEN, nicht als `null` mitgegeben: Der
 * GeoPackage-Schreiber der Bibliothek bestimmt den Spaltentyp am ersten Punkt,
 * der die Eigenschaft traegt. Ein `null` dort machte die ganze Spalte zu Text,
 * und aus 110 wuerde "110". Ohne den Schluessel bestimmt der erste Punkt MIT
 * Zahl den Typ, die uebrigen Punkte bekommen dort NULL - in GeoJSON, GeoPackage
 * und Shapefile gleichermassen. (Im Shapefile heisst die Spalte „Spiegeltie":
 * DBF kuerzt Feldnamen auf zehn Zeichen, wie schon bei „Bemerkungen".)
 */
export function tiefenFuerExport(bp: AufnahmePunktDocument): ExportTiefen {
	const out: ExportTiefen = {};
	const kalk = kalktiefeFuerExport(bp);
	// Spiegeltiefe wie die Kalktiefe: bei einem Filz um die Auflage tiefer.
	const spiegel = angezeigteTiefe(zahlOderNichts(bp.extendedHeader?.spiegelTiefe), filzVersatz(bp));
	if (kalk !== undefined) out.Kalktiefe = kalk;
	if (spiegel !== undefined) out.Spiegeltiefe = spiegel;
	return out;
}

function istZahl(wert: unknown): wert is number {
	return typeof wert === 'number' && Number.isFinite(wert);
}

function zahlOderNichts(wert: unknown): number | undefined {
	return istZahl(wert) ? wert : undefined;
}
